/**
  * @class meleMap_lib
  * @description
  *
  * # Purpose
  * Shared methods
  *
  * # Use
  *
  * # Implementation notes
  *
  */

require('leaflet')

const geohash = require('ngeohash')
const GEOHASH_PRECISION = 12
const DEFAULT_CENTER = 'u2s1yh4mkbre'
const DEFAULT_PANE = 'mosy'
const SEARCH_PANE = 'mosy-search'
const DRAW_PANE = 'mosy-draw'
const TYPE_SHAPE_ATTRIBUTES = {
  unit: { status: '{status}' },
  instruction: { time: 'some:{time}' },
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Converts geohash to [lat,lng]
*/
function geohash2point(geo_hash) {
  let data = []
  if(Array.isArray(geo_hash)) {
    geo_hash.forEach(geo=>{
      data.push(_.toArray(_.omit(geohash.decode(geo), 'error')))
    })
  } else {
    data = _.toArray(_.omit(geohash.decode(geo_hash), 'error'))
  }
  return (data)
} // geohash2point

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Converts [lat,lng] or {lat:...,lng:...} to geohash
*/
function point2geohash(lat_lng, precision) {
  let lat, lng
  if (Array.isArray(lat_lng) && lat_lng.length === 2) {
    lat = parseFloat(lat_lng[0])
    lng = parseFloat(lat_lng[1])
  } else if (_.isObjectLike(lat_lng)) {
    lat = parseFloat(lat_lng.lat)
    lng = parseFloat(lat_lng.lng)
  }
  if (isNaN(lat) || isNaN(lng)) return ''
  return geohash.encode(lat, lng, precision || GEOHASH_PRECISION)
} // point2geohash

function degToRad(d) {
  return d * Math.PI / 180
}
function radToDeg(r) {
  return r * 180 / Math.PI
}
function intermediatePoint(latlng1, latlng2, f) {
  if(!Array.isArray(latlng1)) latlng1 = [latlng1.lat, latlng1.lng]
  if(!Array.isArray(latlng2)) latlng2 = [latlng2.lat, latlng2.lng]
  const lat1=degToRad(latlng1[0])
  const lng1=degToRad(latlng1[1])
  const lat2=degToRad(latlng2[0])
  const lng2=degToRad(latlng2[1])
  const d = 2 * Math.asin(
    Math.sqrt(Math.pow((Math.sin((lat1 - lat2) / 2)),
      2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.pow(Math.sin((lng1-lng2) / 2), 2)))
  const A = Math.sin((1 - f) * d) / Math.sin(d)
  const B = Math.sin(f * d) / Math.sin(d)
  const x = A * Math.cos(lat1) * Math.cos(lng1) + B *
      Math.cos(lat2) * Math.cos(lng2)
  const y = A * Math.cos(lat1) * Math.sin(lng1) + B *
      Math.cos(lat2) * Math.sin(lng2)
  const z = A * Math.sin(lat1) + B * Math.sin(lat2)
  const lat = Math.atan2(z, Math.sqrt(Math.pow(x, 2) +
        Math.pow(y, 2)))
  const lng = Math.atan2(y, x)

  return([radToDeg(lat), radToDeg(lng)])
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
* Default coalescing window for the batched tile loader (ms).
* All createTile() calls within one window are merged into mmFilesGet requests.
*/
const DEFAULT_BATCH_WINDOW_MS = 20

/**
* Maximum number of tiles per single mmFilesGet request. Larger batches risk
* exceeding the websocket / msysMessage payload limit on the local transport
* (observed: a 224-tile reply ~6.7 MB was silently dropped on NWJS).
*/
const DEFAULT_MAX_PER_REQUEST = 32

/**
* Build a Leaflet GridLayer that loads tiles from msysProxy in batches.
* Tiles requested within ~DEFAULT_BATCH_WINDOW_MS are merged into mmFilesGet
* calls (capped at DEFAULT_MAX_PER_REQUEST tiles per call, fired in parallel)
* and the response (binary Uint8Array per tile) is rendered via Blob URL.
*
* @param {object}   opts
* @param {object}   opts.listener         - msysListener instance providing .request()
* @param {string}   opts.repo             - repository / map name (e.g. 'map_osm')
* @param {string}   [opts.format='png']   - file extension and mime hint
* @param {number}   [opts.zIndex=0]       - Leaflet stacking index
* @param {number}   [opts.edgeBufferTiles=0] - leaflet-edgebuffer option
* @param {number}   [opts.batchWindowMs]  - coalescing window in ms
* @param {number}   [opts.maxPerRequest]  - upper bound on tiles per mmFilesGet
* @returns {L.GridLayer}
*/
function createBatchedProxyTileLayer(opts) {
  const listener = opts && opts.listener
  const repo = opts && opts.repo
  if(!listener || !repo) return null

  const format = (opts.format || 'png').toLowerCase()
  const mime = (format === 'jpg' || format === 'jpeg') ? 'image/jpeg' :
    format === 'webp' ? 'image/webp' : 'image/png'
  const zIndex = opts.zIndex || 0
  const edgeBufferTiles = opts.edgeBufferTiles || 0
  const batchWindowMs = _.natural(opts.batchWindowMs) || DEFAULT_BATCH_WINDOW_MS
  const maxPerRequest = _.natural(opts.maxPerRequest) || DEFAULT_MAX_PER_REQUEST

  const pending = [] // [{ uri, z, resolve, reject }]
  let timer = null
  // Filled in below — flush() compares pending tile zooms against this to drop
  // requests for tiles the layer has already moved past (e.g. during pinch zoom,
  // where map.getZoom() reports fractional values that round to a different
  // integer than the layer's _tileZoom).
  let layerInstance = null

  const sendChunk = async (slice) => {
    const uris = slice.map(p => p.uri)
    try {
      const reply = await listener.request('msysProxy', 'mmFilesGet', { uris }, 'mmFiles')
      const files = Array.isArray(reply?.params?.files) ? reply.params.files : []
      const byUri = new Map()
      for(const f of files) if(f && f.uri) byUri.set(f.uri, f)
      for(const p of slice) {
        const r = byUri.get(p.uri)
        if(!r) p.reject(new Error('No result for ' + p.uri))
        else if(r.error) p.reject(new Error(r.error))
        else if(r.bin) p.resolve(r.bin)
        else p.reject(new Error('Empty result for ' + p.uri))
      }
    } catch(err) {
      for(const p of slice) p.reject(err)
    }
  }

  const flush = async () => {
    timer = null
    if(pending.length === 0) return
    const batch = pending.splice(0, pending.length)

    // Compare against the layer's own _tileZoom rather than map.getZoom():
    // during pinch zoom, map._zoom takes on fractional values, but _tileZoom is
    // the integer zoom the layer is actually loading tiles for. If _tileZoom is
    // undefined (not yet set) keep everything.
    const z_now = layerInstance ? layerInstance._tileZoom : undefined
    const live = []
    for(const p of batch) {
      if(z_now === undefined || p.z === z_now) live.push(p)
      else p.reject(new Error('Tile no longer relevant'))
    }
    if(live.length === 0) return

    const chunks = []
    for(let i = 0; i < live.length; i += maxPerRequest) {
      chunks.push(sendChunk(live.slice(i, i + maxPerRequest)))
    }
    await Promise.all(chunks)
  }

  const enqueue = (uri, z) => new Promise((resolve, reject) => {
    pending.push({ uri, z, resolve, reject })
    if(!timer) timer = setTimeout(flush, batchWindowMs)
  })

  const LayerClass = L.GridLayer.extend({
    options: { edgeBufferTiles, zIndex },
    createTile: function(coords, done) {
      const tile = document.createElement('img')
      const size = this.getTileSize()
      tile.width = size.x
      tile.height = size.y
      tile.alt = ''
      tile.onload = () => done(null, tile)
      tile.onerror = (e) => done(e || new Error('tile load error'), tile)

      const uri = repo + '/' + coords.z + '/' + coords.x + '/' + coords.y + '.' + format
      enqueue(uri, coords.z).then(bin => {
        if(!tile.isConnected) {
          done(new Error('Tile disconnected'), tile)
          return
        }
        const url = URL.createObjectURL(new Blob([bin], { type: mime }))
        tile._blobUrl = url
        tile.src = url
      }).catch(err => {
        done(err, tile)
      })

      return tile
    },
    _removeTile: function(key) {
      const tile = this._tiles[key]
      if(tile && tile.el) {
        if(tile.el._blobUrl) {
          try { URL.revokeObjectURL(tile.el._blobUrl) } catch (_) { /* noop */ }
          tile.el._blobUrl = null
        }
        tile.el.onload = null
        tile.el.onerror = null
        tile.el.src = ''
      }
      L.GridLayer.prototype._removeTile.call(this, key)
    },
    getAttribution: function() { return '' },
  })

  layerInstance = new LayerClass()
  return layerInstance
} // createBatchedProxyTileLayer

module.exports = {
  geohash2point,
  point2geohash,
  DEFAULT_CENTER,
  DEFAULT_PANE,
  SEARCH_PANE,
  DRAW_PANE,
  TYPE_SHAPE_ATTRIBUTES,
  intermediatePoint,
  createBatchedProxyTileLayer,
}
